import {
    useLayoutEffect,
    useContext,
    useEffect,
    useState,
    useMemo,
    type FC,
    useRef,
    useId
} from "react";
import {
    useWindowDimensions,
    AccessibilityInfo,
    type TextInput,
    Platform,
    View
} from "react-native";
import type ICommandProps from "./type";
import type {
    CommandKeyPressEventType,
    CommandContextType,
    CommandBlockType
} from "./type";
import stylesheet, {
    getCommandSurfaceColor,
    COMMAND_PAGE_STEP,
    useStyles
} from "./stylesheet";
import CommandDialog from "./components/dialog";
import CommandEmpty from "./components/empty";
import CommandFooter from "./components/footer";
import CommandGroup from "./components/group";
import CommandInput from "./components/input";
import CommandItem from "./components/item";
import CommandList from "./components/list";
import CommandLoading from "./components/loading";
import CommandPage from "./components/page";
import CommandSeparator from "./components/separator";
import {
    CommandDialogContext,
    CommandContext
} from "./context";
import {
    createCommandStore
} from "./store";
import {
    getCommandNavigableItems,
    getCommandVisibleBlocks,
    COMMAND_EMPTY_SCOPE,
    COMMAND_ROOT_SCOPE,
    buildCommandModel
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    formatNumber
} from "../../helpers/locale/format";
import Text from "../text";

const Command: FC<ICommandProps> = ({
    variant: variantProp = "inline",
    filterBehaviour = "auto",
    isLoading = false,
    pages: pagesProp,
    customLocalize,
    onPageChange,
    customTheme,
    children,
    style
}) => {
    const {
        shapeConfigs,
        surfaceColor,
        shadowAlpha,
        elevations,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const dialogContext = useContext(CommandDialogContext);

    const {
        width: windowWidth
    } = useWindowDimensions();

    const baseId = useId();

    const listId = `${baseId}-list`;

    const inputRef = useRef<TextInput>(null);

    const isFirstAnnouncementRef = useRef(true);

    const [
        internalQuery,
        setInternalQuery
    ] = useState("");

    const [
        internalPages,
        setInternalPages
    ] = useState<Array<string>>([]);

    const [
        store
    ] = useState(createCommandStore);

    const variant = dialogContext?.isSheet ? "sheet" : variantProp;

    const model = useMemo(() => buildCommandModel({
        children
    }), [
        children
    ]);

    const query = model.inputProps?.value ?? internalQuery;

    const pages = pagesProp ?? internalPages;

    const pageStack = useMemo(() => pages.flatMap((pageKey) => {
        const page = model.pages[pageKey];

        return page ? [
            page
        ] : [];
    }), [
        model.pages,
        pages
    ]);

    const currentPage = pageStack[pageStack.length - 1];

    const mainBlocks = useMemo(() => getCommandVisibleBlocks({
        entries: currentPage ? currentPage.entries : model.scopes[COMMAND_ROOT_SCOPE] ?? [],
        idPrefix: baseId,
        filterBehaviour,
        query
    }), [
        filterBehaviour,
        model.scopes,
        currentPage,
        baseId,
        query
    ]);

    const resultCount = mainBlocks.reduce((count, block) => count + block.items.length, 0);

    const isEmpty = resultCount === 0 && !isLoading;

    const blocks: Array<CommandBlockType> = useMemo(() => isEmpty ? getCommandVisibleBlocks({
        entries: model.scopes[COMMAND_EMPTY_SCOPE] ?? [],
        filterBehaviour: "manual",
        idPrefix: baseId,
        query: ""
    }) : mainBlocks, [
        model.scopes,
        mainBlocks,
        baseId,
        isEmpty
    ]);

    const navigableItems = useMemo(() => getCommandNavigableItems({
        blocks
    }), [
        blocks
    ]);

    store.setItems(navigableItems);

    const {
        container: containerDynamicStyle,
        status: statusDynamicStyle
    } = useStyles({
        shapeConfigs,
        shadowAlpha,
        windowWidth,
        elevations,
        radiuses,
        borders,
        variant,
        colors,
        spaces
    });

    const resultPluralKey = new Intl.PluralRules(activeLocale).select(resultCount) === "one" ? "command-result-count-one" : "command-result-count-other";

    const resultText = query.length > 0 ? localize(resultPluralKey, [
        formatNumber({
            locale: activeLocale,
            value: resultCount
        })
    ]) : "";

    const announcement = resultText || (currentPage ? localize("command-page-opened", [
        currentPage.title
    ]) : "");

    const firstItemId = navigableItems[0]?.id;

    useEffect(() => {
        if(Platform.OS !== "web" || typeof document === "undefined") {
            return;
        }

        const handleScrollToActive = () => {
            const activeItem = store.getActiveItem();

            if(!activeItem || store.getItemState(activeItem.key) !== 2) {
                return;
            }

            document.getElementById(activeItem.id)?.scrollIntoView({
                block: "nearest"
            });
        };

        handleScrollToActive();

        return store.subscribe(handleScrollToActive);
    }, [
        firstItemId,
        store
    ]);

    useEffect(() => {
        if(isFirstAnnouncementRef.current) {
            isFirstAnnouncementRef.current = false;

            return;
        }

        if(Platform.OS === "web" || !announcement) {
            return;
        }

        AccessibilityInfo.announceForAccessibility(announcement);
    }, [
        announcement
    ]);

    const handleChangeText = (text: string) => {
        setInternalQuery(text);

        store.setActive({
            activeKey: undefined,
            isRingVisible: true
        });

        model.inputProps?.onChangeText?.(text);
    };

    const handlePagesChange = (nextPages: Array<string>) => {
        if(!pagesProp) {
            setInternalPages(nextPages);
        }

        onPageChange?.(nextPages);

        handleChangeText("");
    };

    const handleBack = () => {
        if(!pages.length) {
            return;
        }

        handlePagesChange(pages.slice(0, -1));
    };

    const handleItemPress = ({
        isNewTab,
        key
    }: {
        isNewTab: boolean;
        key: string;
    }) => {
        const item = blocks.flatMap((block) => block.items).find((blockItem) => blockItem.key === key);

        if(!item || item.isDisabled) {
            return;
        }

        item.props.onPress?.({
            isNewTab
        });

        if(item.props.pageKey && model.pages[item.props.pageKey]) {
            handlePagesChange([
                ...pages,
                item.props.pageKey
            ]);

            return;
        }

        dialogContext?.close();
    };

    const handleItemHover = (key: string) => {
        store.setActive({
            isRingVisible: false,
            activeKey: key
        });
    };

    const handleMove = (step: number) => {
        if(!navigableItems.length) {
            return;
        }

        const activeItem = store.getActiveItem();

        const currentIndex = activeItem ? navigableItems.indexOf(activeItem) : -1;

        const nextIndex = Math.min(Math.max(currentIndex + step, 0), navigableItems.length - 1);

        store.setActive({
            activeKey: navigableItems[nextIndex]?.key,
            isRingVisible: true
        });
    };

    const handleSubmit = () => {
        const activeItem = store.getActiveItem();

        if(!activeItem) {
            return;
        }

        handleItemPress({
            key: activeItem.key,
            isNewTab: false
        });
    };

    const handleKeyPress = (event: CommandKeyPressEventType) => {
        const {
            ctrlKey,
            metaKey,
            key
        } = event.nativeEvent;

        const isControlKey = ctrlKey === true && !metaKey;

        const handle = (step: number) => {
            event.preventDefault?.();

            handleMove(step);
        };

        if(key === "ArrowDown" || (isControlKey && key === "n")) {
            handle(1);

            return;
        }

        if(key === "ArrowUp" || (isControlKey && key === "p")) {
            handle(-1);

            return;
        }

        if(key === "PageDown") {
            handle(COMMAND_PAGE_STEP);

            return;
        }

        if(key === "PageUp") {
            handle(-COMMAND_PAGE_STEP);

            return;
        }

        if(key === "Backspace" && !query && pages.length) {
            event.preventDefault?.();

            handleBack();

            return;
        }

        const activeItem = store.getActiveItem();

        if(key === "Enter" && Platform.OS === "web") {
            event.preventDefault?.();
        }

        if(key === "Enter" && Platform.OS === "web" && activeItem) {
            handleItemPress({
                isNewTab: metaKey === true || ctrlKey === true,
                key: activeItem.key
            });
        }
    };

    const handlersRef = useRef({
        onChangeText: handleChangeText,
        onItemHover: handleItemHover,
        onItemPress: handleItemPress,
        onKeyPress: handleKeyPress,
        onSubmit: handleSubmit,
        onBack: handleBack
    });

    useLayoutEffect(() => {
        handlersRef.current = {
            onChangeText: handleChangeText,
            onItemHover: handleItemHover,
            onItemPress: handleItemPress,
            onKeyPress: handleKeyPress,
            onSubmit: handleSubmit,
            onBack: handleBack
        };
    });

    const stableHandlers = useMemo(() => ({
        onItemPress: (props: Parameters<CommandContextType["onItemPress"]>[0]) => {
            handlersRef.current.onItemPress(props);
        },
        onKeyPress: (event: CommandKeyPressEventType) => {
            handlersRef.current.onKeyPress(event);
        },
        onChangeText: (text: string) => {
            handlersRef.current.onChangeText(text);
        },
        onItemHover: (key: string) => {
            handlersRef.current.onItemHover(key);
        },
        onSubmit: () => {
            handlersRef.current.onSubmit();
        },
        onBack: () => {
            handlersRef.current.onBack();
        }
    }), []);

    const contextValue: CommandContextType = useMemo(() => ({
        ...stableHandlers,
        instructionsId: dialogContext?.instructionsId,
        loadingProps: model.loadingProps,
        onClose: dialogContext?.close,
        emptyProps: model.emptyProps,
        inputProps: model.inputProps,
        customLocalize,
        customTheme,
        resultCount,
        isLoading,
        pageStack,
        inputRef,
        isEmpty,
        variant,
        blocks,
        listId,
        store,
        query
    }), [
        dialogContext?.instructionsId,
        model.loadingProps,
        dialogContext?.close,
        model.inputProps,
        model.emptyProps,
        stableHandlers,
        customLocalize,
        resultCount,
        customTheme,
        pageStack,
        isLoading,
        isEmpty,
        variant,
        blocks,
        listId,
        store,
        query
    ]);

    const renderInstructions = () => {
        if(!dialogContext) {
            return null;
        }

        return <View
            style={[
                stylesheet.status,
                statusDynamicStyle
            ]}
        >
            <Text
                id={dialogContext.instructionsId}
                customTheme={customTheme}
            >
                {localize("command-instructions")}
            </Text>
        </View>;
    };

    const renderAnnouncement = () => {
        return <View
            style={[
                stylesheet.status,
                statusDynamicStyle
            ]}
            aria-live="polite"
            role="status"
        >
            {
                Platform.OS === "web" && announcement ?
                    <Text
                        customTheme={customTheme}
                    >
                        {announcement}
                    </Text>
                :
                    null
            }
        </View>;
    };

    return <CommandContext.Provider
        value={contextValue}
    >
        <View
            aria-label={variant === "inline" ? localize("command-commands") : undefined}
            style={[
                style,
                stylesheet.container,
                variant === "sheet" ? stylesheet.sheetContainer : null,
                variant === "dialog" ? stylesheet.dialogContainer : null,
                containerDynamicStyle
            ]}
            role={variant === "inline" ? "region" : undefined}
            aria-busy={isLoading}
        >
            <NCoreUIKitTheme.SurfaceProvider
                surfaceColor={getCommandSurfaceColor({
                    surfaceColor,
                    variant
                })}
            >
                {children}
            </NCoreUIKitTheme.SurfaceProvider>
            {renderInstructions()}
            {renderAnnouncement()}
        </View>
    </CommandContext.Provider>;
};
export default Object.assign(Command, {
    Separator: CommandSeparator,
    Loading: CommandLoading,
    Dialog: CommandDialog,
    Footer: CommandFooter,
    Empty: CommandEmpty,
    Group: CommandGroup,
    Input: CommandInput,
    Item: CommandItem,
    List: CommandList,
    Page: CommandPage
});
export {
    CommandDialog
};
